import React from 'react';
import { showCanvas } from '../app.tsx';
import SaveSpecificationModal from '../ui/save-specification-modal.tsx';
import { getBodyColourImageFilePath, getFilePathForGlossPaintColourId } from '../paintwork-view.tsx';
import { getFilePathForLeatherStyleId } from '../interior/materials/leather-view.tsx';
import { CameraController, CameraViews } from '../../camera-controller.ts';
import { Configuration } from '../../data/configuration.js';
import { SummaryImage } from '../ui/summary-image.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface SummaryStepTwoProps {
	cameraController: CameraController;
	setIsDisabled: (shouldDisable: boolean) => void;
	onSendToDealerClicked: () => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//----------------------------------------------------------------------------------------

export default function SummaryStepTwo(props: SummaryStepTwoProps) {

	// return the HTML.
	return (
		<div className="summary-extra-info-container">
			<SummaryImage cameraController={props.cameraController} setIsDisabled={props.setIsDisabled} className="summary-two-img" />

			<div className="actions-container">
				<SaveSpecificationModal
					cameraController={props.cameraController}
					setIsDisabled={props.setIsDisabled}
					trigger={(openModal: () => void) => (
						<button onClick={openModal} className="save-specification-btn">
							<h3 style={{ margin: 0 }}>Save Specification</h3>
							<img src={'assets/images/icons/up-arrow-white.svg'} alt=">" width="auto" height="12" style={{ transform: 'rotate(90deg)' }} />
						</button>
					)}
				/>
				<button onClick={props.onSendToDealerClicked} className="send-to-dealer-btn">
					<h3 style={{ margin: 0 }}>Send To Dealer</h3>
					<img src={'assets/images/icons/up-arrow.svg'} alt=">" width="auto" height="12" style={{ transform: 'rotate(90deg)' }} />
				</button>
			</div>
		</div>
	);
}


//-----------------------------------------------------------------------------------------
// Public Functions:
//-----------------------------------------------------------------------------------------


export const generatePdfImages = async (cameraController: CameraController) => {

	/*
		this runs from the vehicle panel as well as from the summary, so nothing about the scene can be
		assumed or left behind: on the vehicle panel the canvas stays on screen, the orbital camera is live
		(and would reposition the camera on every frame, overriding the viewpoints being captured), and the
		desktop fov is in force. record all of it, then put it back in restoreScene() on every exit path.
	*/
	const canvasWasVisible = !document.querySelector('#canvas')?.classList.contains('hide');
	const previousView = cameraController.currentView;
	const orbitalWasActive = cameraController.suspendOrbitalCamera();

	const restoreScene = () => {

		// the desktop canvas is 124vw, so it needs its own fov - resetCameraFov() below drops it to the default.
		if (!Configuration.platformIsMobile) {
			cameraController.updateDesktopCameraFov();
		}

		// return the camera to the viewpoint the user was on before re-enabling the orbit around it.
		if (previousView !== null) {
			cameraController.changeCameraView(previousView, undefined, true);
		}
		cameraController.restoreOrbitalCamera(orbitalWasActive);

		showCanvas(canvasWasVisible);
	};

	// reset camera pov before taking images.
	cameraController.resetCameraFov();

	const usePdfv2Template = Configuration.debugUsePdfV2Template;

	// compile list of camera views and their dimensions for the PDF, based on the model.
	const cameraDetails: { cameraView: CameraViews, height: number, width: number }[] = [];
	if (usePdfv2Template) {
		cameraDetails.push({ cameraView: CameraViews.PdfV2ModelView, height: 594, width: 1297 }); // 1.
		cameraDetails.push({ cameraView: CameraViews.PdfV2PaintView1, height: 418, width: 1297 }); // 2.
		cameraDetails.push({ cameraView: CameraViews.PdfV2PaintView2, height: 1149, width: 1297 }); // 3.
		cameraDetails.push({ cameraView: CameraViews.PdfV2WheelView1, height: 553, width: 719 }); // 4.
		cameraDetails.push({ cameraView: CameraViews.PdfV2WheelView2, height: 502, width: 1279 }); // 5.
		cameraDetails.push({ cameraView: CameraViews.PdfV2SeatsView, height: 705, width: 673 }); // 6.
		cameraDetails.push({ cameraView: CameraViews.PdfV2ModelView, height: 1308, width: 1482 }); // 7. n.b. will be replaced below by leather swatch.
		cameraDetails.push({ cameraView: CameraViews.PdfV2DashboardView, height: 308, width: 983 }); // 8.
		cameraDetails.push({ cameraView: CameraViews.PdfV2SteeringWheelView, height: 688, width: 536 }); // 9.
		cameraDetails.push({ cameraView: CameraViews.PdfV2GearstickView, height: 256, width: 390 }); // 10.
		cameraDetails.push({ cameraView: CameraViews.PdfV2CockpitView2, height: 715, width: 986 }); // 11.
		cameraDetails.push({ cameraView: CameraViews.PdfV2RearView, height: 589, width: 716 }); // 12.
		cameraDetails.push({ cameraView: CameraViews.PdfV2ModelView2, height: 598, width: 1453 }); // 13.
		cameraDetails.push({ cameraView: CameraViews.PdfV2CowlView, height: 677, width: 620 }); // 14.
		cameraDetails.push({ cameraView: CameraViews.PdfV2RearView2, height: 343, width: 367 }); // 15.
		cameraDetails.push({ cameraView: CameraViews.AccentPackView, height: 164, width: 176 }); // 16.
		cameraDetails.push({ cameraView: CameraViews.PdfV2LuggageRackView, height: 529, width: 987 }); // 17.
		cameraDetails.push({ cameraView: CameraViews.PdfV2SideView, height: 580, width: 1262 }); // 18.
		cameraDetails.push({ cameraView: CameraViews.PdfV2FullView, height: 862, width: 1532 }); // 19.
		cameraDetails.push({ cameraView: CameraViews.PdfV2ModelView3, height: 1297, width: 1933 }); // 20.	
	}
	else {
		cameraDetails.push({ cameraView: CameraViews.PdfModelView, height: 780, width: 1740 });
		cameraDetails.push({ cameraView: CameraViews.CarpetView, height: 160, width: 280 });	// n.b. replaced below by the body paint swatch - the camera view is never rendered, only the size is used.
		cameraDetails.push({ cameraView: CameraViews.PdfPaintworkView, height: 780, width: 1880 });
		cameraDetails.push({ cameraView: CameraViews.LeftWheelView, height: 600, width: 920 });
		cameraDetails.push({ cameraView: CameraViews.PdfWheelView, height: 600, width: 920 });
		cameraDetails.push({ cameraView: CameraViews.SeatsView, height: 1260, width: 910 });
		cameraDetails.push({ cameraView: CameraViews.ConsoleView, height: 960, width: 1880 });
		cameraDetails.push({ cameraView: CameraViews.PdfSideView, height: 600, width: 1370 });
		cameraDetails.push({ cameraView: CameraViews.HoodPipingView, height: 600, width: 1370 });
		cameraDetails.push({ cameraView: CameraViews.CowlView, height: 970, width: 1240 });
		cameraDetails.push({ cameraView: CameraViews.TunnelStorageView, height: 600, width: 1240 });
		cameraDetails.push({ cameraView: CameraViews.SpotLightView, height: 600, width: 1240 });
		cameraDetails.push({ cameraView: CameraViews.HoodView, height: 1250, width: 1890 });
	}

	// set the initial camera view to the first one in the list.
	cameraController.changeCameraView(cameraDetails[0].cameraView, () => null, true);

	// add canvas to the DOM.
	showCanvas(true);

	// handle if there is no canvas.
	const canvas = document.querySelector('#canvas') as HTMLCanvasElement;
	if (!canvas) {
		console.error('Canvas not found!');
		restoreScene();
		return null;
	}

	// don't actually show canvas to the user.
	canvas.style.opacity = '0';

	// brief time out to give canvas time to render.
	await new Promise((resolve) => setTimeout(resolve, 500));

	const imageBlobs: Blob[] = [];
	for (let i = 0; i < cameraDetails.length; i++) {
		const currentView = cameraDetails[i];
		let imgBlob: Blob;
		
		// replace the relevant image with the leather swatch.
		if (usePdfv2Template && i === 6) {
			const leatherImageFilePath = getFilePathForLeatherStyleId(Configuration.leatherStyle);
			const response = await fetch(`assets/images/configuration-items/interior/leather/${leatherImageFilePath}.webp`);
			const leatherImage = await response.blob();
			imgBlob = await convertWebPToCroppedJpg(leatherImage, currentView.width, currentView.height);
		}

		// or replace the relevant image with the paintwork swatch.
		else if (!usePdfv2Template && i === 1) {
			const paintImageFilePath = getBodyColourImageFilePath(getFilePathForGlossPaintColourId);
			const response = await fetch(paintImageFilePath);
			const paintworkImage = await response.blob();
			imgBlob = await convertWebPToCroppedJpg(paintworkImage, currentView.width, currentView.height);
		} 

		// othwerwise, change the camera view and take a screenshot.
		else {
			cameraController.changeCameraView(currentView.cameraView, () => null, true);

			// give canvas time to render the new view.
			await new Promise((resolve) => requestAnimationFrame(() => resolve({})));
			const scaledCanvas = cropVerticallyAndResize(canvas, currentView.height, currentView.width);

			imgBlob = await new Promise(resolve =>
				scaledCanvas.toBlob(
					blob => resolve(blob as Blob),
					'image/jpeg',
					0.7
				)
			) as Blob;
		}
		imageBlobs.push(imgBlob);
	}

	canvas.style.opacity = '1';
	restoreScene();

	return imageBlobs;
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------


// handle resizing images properly based on the dimensions provided by sur, crops image's height to create the aspect ratio then resizes.
const cropVerticallyAndResize = (
	canvas: HTMLCanvasElement,
	targetHeight: number,
	targetWidth: number
): HTMLCanvasElement => {
	const srcW = canvas.width;
	const srcH = canvas.height;

	// desired aspect ratio, expressed as width / height.
	const targetAspect = targetWidth / targetHeight;

	// figure out how much height we need to keep if we keep full width.
	let cropH = srcW / targetAspect;

	// if the source isn’t tall enough, fall back to cropping width instead.
	let sx: number = 0, sy: number, sw: number = srcW, sh: number = cropH;
	if (cropH > srcH) {
		// need to crop width instead.
		cropH = srcH;
		sh = cropH;
		sw = srcH * targetAspect;
		sx = (srcW - sw) / 2;   // centre crop horizontally.
		sy = 0;
	} else {

		// normal path – centre crop vertically.
		sy = (srcH - cropH) / 2;
	}

	// create a new canvas at the final size.
	const out = document.createElement('canvas');
	out.width = targetWidth;
	out.height = targetHeight;
	const ctx = out.getContext('2d');
	if (!ctx) return canvas;

	// draw the cropped region into the new canvas, letting drawImage handle the scaling for us.
	ctx.drawImage(
		canvas,
		sx, sy, sw, sh,
		0, 0, targetWidth, targetHeight
	);

	return out;
};

// car paint images are stored as webp and sur requires jpeg.
const convertWebPToCroppedJpg = async (
	webpBlob: Blob,
	targetWidth: number,
	targetHeight: number
): Promise<Blob> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			// create a temp canvas with the image's original size.
			const originalCanvas = document.createElement('canvas');
			originalCanvas.width = img.width;
			originalCanvas.height = img.height;
			const originalCtx = originalCanvas.getContext('2d');
			if (!originalCtx) return reject(new Error('Failed to get canvas context'));
			originalCtx.drawImage(img, 0, 0);

			// crop and resize using the previous logic.
			const croppedCanvas = cropVerticallyAndResize(originalCanvas, targetHeight, targetWidth);

			// export as JPEG blob.
			croppedCanvas.toBlob((blob) => {
				if (blob) resolve(blob);
				else reject(new Error('Failed to convert to JPEG'));
			}, 'image/jpeg', 0.92);
		};

		img.onerror = () => reject(new Error('Failed to load image'));
		img.src = URL.createObjectURL(webpBlob);
	});
};